/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Image 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/image/Image.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 11.
 */
import * as parent from '@components/form/field/file/File';
import * as uploader from '@utils/Uploader';
import { Dom } from '@utils/Dom';
import { Ajax } from '@utils/Ajax';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import { Dragger } from '@utils/Dragger';
import { Exceptions } from '@utils/Exceptions';
import './Image.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 확장자를 제외한 이미지명 (NULL 인 경우 원본파일명을 그대로 사용한다.)
     */
    imageName?: string;

    /**
     * 이미지 너비
     */
    imageWidth?: number;

    /**
     * 이미지 높이
     */
    imageHeight?: number;

    /**
     * 이미지 미리보기 스케일
     */
    previewScale?: number;

    /**
     * 이미지 크기를 보여줄지 여부
     */
    showSize?: boolean;

    /**
     * 이미지 편집 가능여부
     */
    editable?: boolean;

    /**
     * 파일이 업로드되지 않았을 경우 보일 기본 이미지
     */
    defaultImage?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Image extends parent.File {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'image';

    /**
     * 확장자를 제외한 이미지명 (NULL 인 경우 원본파일명을 그대로 사용한다.)
     */
    imageName: string;

    /**
     * 이미지 너비
     */
    imageWidth: number;

    /**
     * 이미지 높이
     */
    imageHeight: number;

    /**
     * 이미지 미리보기 스케일
     */
    previewScale: number;

    /**
     * 이미지 크기를 보여줄지 여부
     */
    showSize: boolean | null = null;

    /**
     * 이미지 편집 가능여부
     */
    editable: boolean;

    /**
     * 이미지 미리보기 DOM
     */
    $preview: Dom = null;

    /**
     * 이미지 크기 DOM
     */
    $size: Dom = null;

    /**
     * 삭제버튼 DOM
     */
    $delete: Dom = null;

    /**
     * 프로그래스바 DOM
     */
    $progress: { read: Dom; upload: Dom } = { read: null, upload: null };

    /**
     * 이미지 확대/축소 range input DOM
     */
    $zoomer: Dom = null;

    /**
     * 이미지 편집 DOM
     */
    $editor: Dom = null;

    /**
     * 이미지 편집툴
     */
    $tools: Dom = null;

    /**
     * 편집모드시 이미지 드래그 핸들러
     */
    dragger: Dragger = null;

    /**
     * 편집모드시 선택된 원본 이미지 엘리먼트
     */
    editImage: HTMLImageElement | null = null;

    /**
     * 편집모드시 원본 이미지 ObjectURL
     */
    editImageUrl: string | null = null;

    /**
     * 편집모드시 선택된 원본 파일명 (imageName 이 NULL 일 경우 이 이름을 그대로 사용한다.)
     */
    originalFileName: string | null = null;

    /**
     * 편집모드시 preview를 꽉 채우는 최소 배율 (zoom=0 기준)
     */
    imageMinScale: number = 1;

    /**
     * 편집모드시 현재 배율
     */
    imageScale: number = 1;

    /**
     * 편집모드시 드래그 오프셋
     */
    dragStartOffsetX: number = 0;
    dragStartOffsetY: number = 0;

    /**
     * editable 모드 — 이미지 X 오프셋 (px, preview 좌측 상단 기준)
     */
    imageOffsetX: number = 0;

    /**
     * editable 모드 — 이미지 Y 오프셋 (px, preview 좌측 상단 기준)
     */
    imageOffsetY: number = 0;

    /**
     * 이미지 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Omit<Properties<Image>, 'multiple'> = null) {
        super(properties);

        this.url = this.properties?.url ?? parent.File.url ?? '/';
        this.accept = this.properties?.accept ?? 'image/*';
        this.imageName = this.properties?.imageName ?? null;

        this.text = this.properties?.text ?? Language.get('components.form.field.image.select');
        this.icon = this.properties?.icon ?? 'mi mi-image';
        this.showSize = this.properties?.showSize === true;
        this.editable = this.properties?.editable === true;

        /**
         * 이미지 편집이 가능한 경우, 이미지 선택 즉시 파일을 업로드하지 않고 편집된 이미지를 업로드한다.
         */
        if (this.editable === true) {
            this.autoUpload = false;
        }

        this.setImageSize(
            this.properties?.imageWidth ?? 100,
            this.properties?.imageHeight ?? 100,
            this.properties?.previewScale ?? 1
        );
    }

    /**
     * 업로더를 가져온다.
     *
     * @returns 업로더
     */
    getUploader(): uploader.Uploader {
        if (this.uploader === null) {
            this.uploader = new uploader.Uploader({
                url: this.url,
                update: async (draft_id, attachment, status) => this.#update(draft_id, attachment, status),
            });
        }

        return this.uploader;
    }

    /**
     * 파일 INPUT Dom을 가져온다.
     *
     * @returns 파일 INPUT
     */
    $getInput(): Dom {
        if (this.$input == null) {
            this.$input = Html.create('input', { type: 'file', accept: this.accept });

            this.$input.on('change', async (me, e: Event & { target: HTMLInputElement }) => {
                if (e.target.files.length == 0) {
                    return;
                }

                const isImage = this.isImage(e.target.files[0].name, e.target.files[0].type);
                if (isImage == true) {
                    await this.#delete();

                    if (this.editable == true) {
                        await this.#loadEditableImage(e.target.files[0]);
                    } else {
                        for (const attachment of this.getUploader().add(this.#renameFiles(e.target.files))) {
                            this.attachments.set(attachment.attachment_id, attachment);
                            this.#print(attachment);
                        }
                    }

                    me.reset();

                    if (this.autoUpload == true) {
                        this.uploader.start();
                    }
                }
            });
        }

        return this.$input;
    }

    /**
     * 파일목록 DOM 을 가져온다.
     *
     * @return 파일목록 DOM
     */
    $getFiles(): Dom {
        /**
         * 이 DOM 은 이미지 프리뷰보다 가로 세로 패딩이 있다.
         * 이미지 편집모드시 여기에도 이미지를 채워 이미지가 크롭되는 경계의 바깥 이미지를 일부 보여준다.
         */
        if (this.$files === null) {
            this.$files = Html.create('div', { 'data-role': 'files' });

            this.$files.on('drop', async (_, e: DragEvent) => {
                if (e.dataTransfer?.types.includes('Files') == true) {
                    e.preventDefault();

                    const dropped = Array.from(e.dataTransfer.files);
                    const files = dropped.slice(0, 1);

                    const isImage = this.isImage(files[0].name, files[0].type);
                    if (isImage == true) {
                        await this.#delete();

                        if (this.editable == true) {
                            await this.#loadEditableImage(files[0]);
                        } else {
                            for (const attachment of this.getUploader().add(this.#renameFiles(files))) {
                                this.attachments.set(attachment.attachment_id, attachment);
                                this.#print(attachment);
                            }
                        }

                        if (this.autoUpload == true) {
                            this.getUploader().start();
                        }
                    }
                }
            });
        }

        return this.$files;
    }

    /**
     * 이미지 미리보기 DOM을 가져온다.
     *
     * @returns 이미지 미리보기 DOM
     */
    $getPreview(): Dom {
        /**
         * 이 영역에 들어가는 이미지가 실제로 크롭되는 이미지. 크롭되는 이미지 크기는 this.imageWidth, this.imageHeight 가 되어야한다.
         * editable 모드일 경우 이 영역에서 Dragger 를 통해 이미지 위치를 조절할 수 있다.
         */
        if (this.$preview == null) {
            this.$preview = Html.create('div', { 'data-role': 'image' });

            if (this.editable == true) {
                this.dragger = new Dragger(this.$preview, {
                    cursor: 'grabbing',
                    isActive: () => {
                        return this.editImage !== null;
                    },
                    listeners: {
                        start: () => {
                            if (this.editImage === null) return;
                            this.dragStartOffsetX = this.imageOffsetX;
                            this.dragStartOffsetY = this.imageOffsetY;
                        },
                        drag: (_, dragger) => {
                            if (this.editImage === null) return;
                            const length = dragger.getLength();
                            this.imageOffsetX = this.dragStartOffsetX + length.x;
                            this.imageOffsetY = this.dragStartOffsetY + length.y;
                            this.#clampOffset();
                            this.#updatePreviewTransform();
                        },
                    },
                });
            }
        }

        return this.$preview;
    }

    /**
     * 이미지 편집도구 DOM 을 가져온다.
     *
     * @returns 이미지 편집도구 DOM
     */
    $getEditor(): Dom {
        if (this.$editor === null) {
            this.$editor = Html.create('div', { 'data-role': 'editor' });
        }

        return this.$editor;
    }

    /**
     * 이미지 편집툴 DOM 을 가져온다.
     *
     * @returns 이미지 편집툴 DOM
     */
    $getTools(): Dom {
        if (this.$tools === null) {
            this.$tools = Html.create('div', { 'data-role': 'tools' });

            const $rotateLeft = Html.create('button', { type: 'button', 'data-rotate': 'left' }, '<i></i>');
            $rotateLeft.on('click', async (me) => {
                me.setDisabled(true);
                await this.#rotateImage('left');
                me.setDisabled(false);
            });

            const $rotateRight = Html.create('button', { type: 'button', 'data-rotate': 'right' }, '<i></i>');
            $rotateRight.on('click', async (me) => {
                me.setDisabled(true);
                await this.#rotateImage('right');
                me.setDisabled(false);
            });

            this.$tools.append($rotateLeft);
            this.$tools.append(this.$getZoomer());
            this.$tools.append($rotateRight);
        }

        return this.$tools;
    }

    /**
     * 이미지 편집모드시 이미지 확대/축소를 위한 Range INPUT DOM 을 가져온다.
     * value 0 → imageMinScale (preview를 꽉 채우는 최소 크기)
     * value 100 → imageMinScale × 4 (4배 확대)
     *
     * @returns 이미지 확대범위 DOM
     */
    $getZoomer(): Dom {
        if (this.$zoomer === null) {
            this.$zoomer = Html.create('input', {
                type: 'range',
                'data-role': 'zoomer',
                min: '0',
                max: '100',
                value: '0',
                step: '1',
            });

            this.$zoomer.on('input', (_, e: InputEvent) => {
                if (this.editImage === null) return;
                const ratio = parseFloat((e.target as HTMLInputElement).value) / 100;
                const newScale = this.imageMinScale * (1 + 3 * ratio);

                /**
                 * 줌 변경 시 preview 중앙이 이미지의 동일한 지점에 유지되도록 offset 을 재계산한다.
                 * 현재 중앙이 원본 이미지 좌표계에서 어디인지 역산한 뒤, 새 scale 기준으로 다시 중앙에 배치한다.
                 */
                const { width: previewW, height: previewH } = this.#getPreviewSize();
                const centerX = previewW / 2;
                const centerY = previewH / 2;
                const anchorImgX = (centerX - this.imageOffsetX) / this.imageScale;
                const anchorImgY = (centerY - this.imageOffsetY) / this.imageScale;
                this.imageOffsetX = centerX - anchorImgX * newScale;
                this.imageOffsetY = centerY - anchorImgY * newScale;

                this.imageScale = newScale;
                this.#clampOffset();
                this.#updatePreviewTransform();
            });
        }

        return this.$zoomer;
    }

    /**
     * 이미지 크기표시 DOM을 가져온다.
     *
     * @returns 이미지 크기표시 DOM
     */
    $getSize(): Dom {
        if (this.$size == null) {
            this.$size = Html.create('div', { 'data-role': 'size' });
        }

        return this.$size;
    }

    /**
     * 삭제버튼 DOM 을 가져온다.
     *
     * @returns 삭제버튼 DOM
     */
    $getDelete(): Dom {
        if (this.$delete == null) {
            this.$delete = Html.create('button', { type: 'button', 'data-action': 'delete' }, '<i></i>');
            this.$delete.on('click', () => {
                this.#delete();
            });
        }

        return this.$delete;
    }

    /**
     * 프로그래스바 DOM를 가져온다.
     *
     * @param type - 가져올 프로그래스바 타입
     * @returns 프로그래스바 DOM
     */
    $getProgress(type: 'read' | 'upload'): Dom {
        if (this.$progress[type] === null) {
            this.$progress[type] = Html.create('progress', {
                'data-role': type,
                min: '0',
                max: '100',
                value: '0',
            });
        }

        return this.$progress[type];
    }

    /**
     * 파일을 선택한다.
     */
    async select(): Promise<void> {
        this.$getInput().getEl().click();
    }

    /**
     * 파일종류가 이미지인지 확인한다.
     *
     * @param name - 파일명
     * @param mime - 파일 MIME
     * @returns 이미지파일여부
     */
    isImage(name: string, mime: string): boolean {
        mime ??= '';
        if (mime.search(/^image\/(.*?)$/) === 0) {
            return true;
        }

        Exceptions.error('IS_NOT_IMAGE', name, mime);
        return false;
    }

    /**
     * 이미지 미리보기 크기를 조절한다.
     *
     * @param imageWidth - 가로크기
     * @param imageHeight - 세로크기
     * @param previewScale - 미리보기 스케일
     */
    setImageSize(imageWidth: number, imageHeight: number, previewScale: number = 1): void {
        this.imageWidth = imageWidth ?? 100;
        this.imageHeight = imageHeight ?? 100;
        this.previewScale = previewScale ?? 1;

        if (this.imageWidth < 30 || this.imageHeight < 30) {
            this.previewScale = Math.max(
                this.previewScale,
                parseFloat((30 / this.imageWidth).toFixed(2)),
                parseFloat((30 / this.imageHeight).toFixed(2))
            );
        }

        this.$getPreview().setStyle('aspect-ratio', `${this.imageWidth} / ${this.imageHeight}`);
        this.$getPreview().setStyle('width', `${Math.ceil(this.imageWidth * this.previewScale)}px`);

        if (this.showSize == true && this.imageWidth !== null && this.imageHeight !== null) {
            this.$getSize().html(this.imageWidth + '&times;' + this.imageHeight);
        } else {
            this.$getSize().empty();
        }
    }

    /**
     * 필드값이 비어있는지 확인한다.
     *
     * @returns 빈값여부
     */
    async isBlank(value: any = undefined): Promise<boolean> {
        if (this.editable == true) {
            return this.editImage === null && this.attachments.size == 0;
        }

        return this.attachments.size == 0;
    }

    /**
     * 업로드가 완료된 파일의 고유값을 가져온다.
     *
     * @return 첨부파일고유값
     */
    async getValue(): Promise<string> {
        if (this.editable == true) {
            /**
             * 편집된 이미지가 없으면 (setValue로 기존 이미지가 설정된 경우) 기존 값 반환
             */
            if (this.editImage === null) {
                const value = this.attachments.values().toArray();
                this.value = value.at(-1)?.attachment_id ?? null;
                return this.value;
            }

            /**
             * preview 영역을 canvas로 크롭하여 Blob을 만들고, File 객체로 변환 후 업로드한다.
             * canvas.toBlob() 은 브라우저가 image/webp 인코딩을 지원하지 않으면 image/png 로 조용히 폴백하므로,
             * 실제로 반환된 blob.type 을 신뢰해서 확장자를 정한다.
             */
            const blob = await this.#cropImage();
            const fileName = this.#buildFileName(this.originalFileName ?? 'photo.png', blob.type, true);
            const croppedFile = new globalThis.File([blob], fileName, { type: blob.type });

            for (const attachment of this.getUploader().add([croppedFile])) {
                this.attachments.set(attachment.attachment_id, attachment);
            }

            const attachments = await this.getUploader().get();
            const attachment = attachments.at(-1) ?? null;
            if (attachment !== null) {
                this.attachments.set(attachment.attachment_id, attachment);
            }

            this.value = attachment?.attachment_id ?? null;
            return this.value;
        } else {
            /**
             * 새로 업로드된 파일이 확인하고, 새로 추가된 파일이 알지못하는 파일일 경우 예외를 출력한다.
             */
            const attachments = await this.getUploader().get();
            for (const attachment of attachments) {
                if (this.attachments.has(attachment.attachment_id) == false) {
                    throw Exceptions.code('UNKNOWN_FILE');
                }
            }

            const value = this.attachments.values().toArray();
            this.value = value.at(-1)?.attachment_id ?? null;

            return this.value;
        }
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: string, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        this.attachments.clear();

        if (value === null) {
            this.attachments.clear();
        } else {
            const results = await Ajax.get(this.url, { attachment_ids: JSON.stringify([value]) });

            if (results.success == true) {
                for (const attachment of results.attachments) {
                    this.attachments.set(attachment.id, attachment);
                    this.#print(attachment);

                    if (this.multiple == false) {
                        break;
                    }
                }
            }
        }

        await this.releaseValue();
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getFiles().append(this.$getPreview());
        this.$getFiles().append(this.$getSize());
        this.$getFiles().append(this.$getProgress('read'));
        this.$getFiles().append(this.$getProgress('upload'));
        this.$getFiles().append(this.$getDelete());

        if (this.editable == true) {
            this.$getFiles().setAttr('data-editable', 'true');
            const $editor = this.$getEditor();
            $editor.append(this.$getFiles());
            $editor.append(this.$getTools());
            this.$getTools().hide();
            this.$getContent().append($editor);
        } else {
            this.$getContent().append(this.$getFiles());
        }

        this.$getContent().append(this.getButton().$getComponent());
        await this.getButton().render(true);
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - DOM 제거 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (this.editImageUrl !== null) {
            URL.revokeObjectURL(this.editImageUrl);
            this.editImageUrl = null;
        }
        await super.remove(removeDom);
    }

    /**
     * 업로드 상태에 따라 파일을 갱신한다.
     *
     * @param draft_id - 임시파일고유값
     * @param attachment - 첨부파일객체
     * @param status - 업로드상태
     */
    #update(draft_id: string, attachment: uploader.Attachment, status: uploader.Status): void {
        this.$getFiles().setAttr('data-status', attachment.status);

        /**
         * 업로드중인 파일이라면 업로드 상태에 따라 진행상황을 업데이트한다.
         */
        if (attachment.status == 'LOCAL') {
            this.$getFiles().setAttr('data-upload-status', status.status);

            if (status.status == 'READING') {
                const progress = Math.round((status.readed / attachment.size) * 100).toString();
                this.$getProgress('read').setStyleProperty('--progress', progress);
                this.$getProgress('read').setAttr('value', progress);
            }

            if (status.status == 'UPLOADING') {
                const progress = Math.round((status.uploaded / attachment.size) * 100).toString();
                this.$getProgress('upload').setStyleProperty('--progress', progress);
                this.$getProgress('upload').setAttr('value', progress);
            }

            /**
             * 업로드가 실패하면 필드에 실패 마킹을 할 곳이 없으므로, 성공한 파일이 없었던 것처럼 초기화한다.
             * getValue() 호출시 Uploader.get() 이 던지는 예외로 Form 에 실패를 알린다.
             */
            if (status.status == 'FAILED') {
                this.#reset();
            }
        } else {
            this.$getFiles().removeAttr('data-upload-status');

            this.attachments.delete(draft_id);
            this.attachments.set(attachment.attachment_id, attachment);
            this.#print(attachment);
        }
    }

    /**
     * 파일을 출력한다.
     *
     * @param attachment - 출력할 파일객체
     */
    #print(attachment: uploader.Attachment): void {
        this.$getFiles().setAttr('data-status', attachment.status);

        /**
         * editable 모드에서 편집 중인 이미지가 있다면 #updatePreviewTransform 이 담당하므로 배경이미지를 덮어쓰지 않는다.
         */
        if (this.editable == false || this.editImage === null) {
            const $preview = this.$getPreview();
            $preview.setStyle('background-image', `url(${attachment.download})`);
        }

        if (attachment.status == 'LOCAL') {
            const status = this.uploader.getStatus(attachment.attachment_id);
            this.#update(attachment.attachment_id, attachment, status);
        }
    }

    /**
     * 파일을 제거한다.
     */
    async #delete(): Promise<void> {
        await this.getUploader().pause();
        for (const attachment of this.attachments.values()) {
            if (attachment.status == 'LOCAL') {
                this.getUploader().delete(attachment.attachment_id);
            }
        }
        this.#reset();
    }

    /**
     * 필드에 파일이 없는 초기 상태로 되돌린다. (미리보기, 상태값, 편집상태를 모두 제거)
     */
    #reset(): void {
        this.attachments.clear();

        this.$getPreview().setStyle('background-image', null);
        this.$getFiles().removeAttr('data-status');
        this.$getFiles().removeAttr('data-upload-status');

        if (this.editable == true) {
            if (this.editImageUrl !== null) {
                URL.revokeObjectURL(this.editImageUrl);
                this.editImageUrl = null;
            }
            this.editImage = null;
            this.originalFileName = null;
            this.$getPreview().setStyle('background-size', null);
            this.$getPreview().setStyle('background-position', null);
            this.$getFiles().setStyleProperty('--edit-bg-image', 'none');
            this.$getTools().hide();
            this.$getFiles().removeAttr('data-has-image');
        }
    }

    /**
     * editable 모드에서 선택한 파일을 이미지로 로드하고 편집 상태를 초기화한다.
     *
     * @param file - 선택된 파일
     */
    async #loadEditableImage(file: globalThis.File): Promise<void> {
        if (this.editImageUrl !== null) {
            URL.revokeObjectURL(this.editImageUrl);
            this.editImageUrl = null;
            this.editImage = null;
        }

        this.originalFileName = file.name;
        this.editImageUrl = URL.createObjectURL(file);

        await new Promise<void>((resolve) => {
            const img = document.createElement('img');
            img.onload = () => {
                this.editImage = img;

                const { width: previewW, height: previewH } = this.#getPreviewSize();
                const minScale = Math.max(previewW / img.naturalWidth, previewH / img.naturalHeight);
                this.imageMinScale = minScale;
                this.imageScale = minScale;
                this.imageOffsetX = (previewW - img.naturalWidth * minScale) / 2;
                this.imageOffsetY = (previewH - img.naturalHeight * minScale) / 2;

                (this.$getZoomer().getEl() as HTMLInputElement).value = '0';
                this.$getTools().show();
                this.$getFiles().setAttr('data-has-image', 'true');
                this.#updatePreviewTransform();
                resolve();
            };
            img.src = this.editImageUrl;
        });
    }

    /**
     * canvas 를 편집 이미지로 적용한다.
     * blob → ObjectURL → img 로드 순으로 처리하며, minScale/offset/zoomer 를 재초기화하고 transform 을 업데이트한다.
     *
     * @param canvas - 적용할 canvas
     */
    async #applyCanvasToEdit(canvas: HTMLCanvasElement): Promise<void> {
        const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.92));

        if (this.editImageUrl !== null) {
            URL.revokeObjectURL(this.editImageUrl);
        }
        this.editImageUrl = URL.createObjectURL(blob);

        await new Promise<void>((resolve) => {
            const img = document.createElement('img');
            img.onload = () => {
                this.editImage = img;

                const { width: previewW, height: previewH } = this.#getPreviewSize();
                const minScale = Math.max(previewW / img.naturalWidth, previewH / img.naturalHeight);
                this.imageMinScale = minScale;
                this.imageScale = minScale;
                this.imageOffsetX = (previewW - img.naturalWidth * minScale) / 2;
                this.imageOffsetY = (previewH - img.naturalHeight * minScale) / 2;

                (this.$getZoomer().getEl() as HTMLInputElement).value = '0';
                this.#updatePreviewTransform();
                resolve();
            };
            img.src = this.editImageUrl;
        });
    }

    /**
     * 이미지를 90도 단위로 회전한다.
     *
     * @param direction - 회전 방향
     */
    async #rotateImage(direction: 'left' | 'right'): Promise<void> {
        if (this.editImage === null) return;

        const img = this.editImage;
        const radians = direction === 'left' ? -Math.PI / 2 : Math.PI / 2;

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalHeight;
        canvas.height = img.naturalWidth;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(radians);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

        await this.#applyCanvasToEdit(canvas);
    }

    /**
     * imageOffsetX/Y 가 이미지가 preview 영역을 벗어나지 않도록 범위를 제한한다.
     */
    #clampOffset(): void {
        if (this.editImage === null) return;

        const displayW = this.editImage.naturalWidth * this.imageScale;
        const displayH = this.editImage.naturalHeight * this.imageScale;
        const { width: previewW, height: previewH } = this.#getPreviewSize();

        this.imageOffsetX = Math.min(0, Math.max(previewW - displayW, this.imageOffsetX));
        this.imageOffsetY = Math.min(0, Math.max(previewH - displayH, this.imageOffsetY));
    }

    /**
     * 현재 imageScale/imageOffsetX/Y 를 preview DOM의 background-size, background-position 에 반영한다.
     * $getFiles() 의 ::before 에도 동일한 이미지를 padding 오프셋만큼 보정하여 반투명하게 표시한다.
     */
    #updatePreviewTransform(): void {
        if (this.editImage === null) return;

        const displayW = this.editImage.naturalWidth * this.imageScale;
        const displayH = this.editImage.naturalHeight * this.imageScale;

        this.$getPreview().setStyle('background-image', `url(${this.editImageUrl})`);
        this.$getPreview().setStyle('background-size', `${displayW}px ${displayH}px`);
        this.$getPreview().setStyle('background-position', `${this.imageOffsetX}px ${this.imageOffsetY}px`);

        /**
         * $getPreview() 의 offsetLeft/Top 이 $getFiles() 의 padding 값과 같으므로,
         * 그만큼 오프셋을 더해 $getFiles()::before 의 배경이미지가 $getPreview() 와 정확히 일치하게 한다.
         */
        const padLeft = this.$getPreview().getEl().offsetLeft;
        const padTop = this.$getPreview().getEl().offsetTop;

        this.$getFiles().setStyleProperty('--edit-bg-image', `url(${this.editImageUrl})`);
        this.$getFiles().setStyleProperty('--edit-bg-size', `${displayW}px ${displayH}px`);
        this.$getFiles().setStyleProperty(
            '--edit-bg-position',
            `${this.imageOffsetX + padLeft}px ${this.imageOffsetY + padTop}px`
        );
    }

    /**
     * preview 영역 기준으로 이미지를 canvas에 크롭하여 Blob을 반환한다.
     * 출력 크기는 this.imageWidth × this.imageHeight.
     *
     * @returns 크롭된 이미지 Blob
     */
    async #cropImage(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            canvas.width = this.imageWidth;
            canvas.height = this.imageHeight;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            /**
             * imageOffsetX/Y 는 표시 이미지 좌측 상단이 preview 좌측 상단(실제 DOM 크기, previewScale 반영)으로부터
             * 얼마나 떨어졌는지를 나타낸다. 원본 이미지 좌표로 환산하려면 현재 scale로 나눈다.
             * 출력 해상도는 previewScale 과 무관하게 this.imageWidth × this.imageHeight 로 고정한다.
             */
            const { width: previewW, height: previewH } = this.#getPreviewSize();
            const srcX = -this.imageOffsetX / this.imageScale;
            const srcY = -this.imageOffsetY / this.imageScale;
            const srcW = previewW / this.imageScale;
            const srcH = previewH / this.imageScale;

            ctx.drawImage(this.editImage, srcX, srcY, srcW, srcH, 0, 0, this.imageWidth, this.imageHeight);

            canvas.toBlob(
                (blob) => {
                    if (blob !== null) {
                        resolve(blob);
                    } else {
                        reject(new Error('Canvas toBlob failed'));
                    }
                },
                'image/webp',
                0.92
            );
        });
    }

    /**
     * imageName 설정에 따라 실제로 사용할 파일명을 결정한다.
     * imageName 이 지정된 경우 imageName + 확장자를 사용하고, NULL 인 경우 원본파일명을 그대로 사용한다.
     *
     * 확장자 우선순위는 두 가지 경우가 다르다.
     * - 원본 파일을 그대로 올리는 경우(preferMimeExtension=false) : 원본파일명의 확장자가 브라우저의 MIME 추정보다 정확하다.
     *   (예 : favicon.ico 의 MIME 이 image/x-icon 이라 MIME 기준으로는 확장자가 x-icon 이 되어버린다.)
     * - 캔버스에서 인코딩한 blob(preferMimeExtension=true) : 원본 확장자와 실제 인코딩 결과가 다를 수 있으므로
     *   (예 : image/webp 미지원시 image/png 로 조용히 폴백) blob.type 을 신뢰해야 한다.
     *
     * @param originalName - 원본 파일명
     * @param mime - 실제 파일 MIME
     * @param preferMimeExtension - MIME 기준 확장자를 우선할지 여부 (기본값 : false)
     * @returns 사용할 파일명
     */
    #buildFileName(originalName: string, mime: string, preferMimeExtension: boolean = false): string {
        if (this.imageName === null) {
            return originalName;
        }

        const mimeExtension = mime?.split('/').pop();
        const originalExtension = originalName.includes('.') ? originalName.split('.').pop() : null;
        const extension = preferMimeExtension
            ? mimeExtension || originalExtension || 'png'
            : originalExtension || mimeExtension || 'png';

        return `${this.imageName}.${extension}`;
    }

    /**
     * imageName 이 지정된 경우, 업로드할 파일들의 이름을 imageName 기준으로 변경한다.
     *
     * @param files - 원본 파일목록
     * @returns 파일명이 적용된 파일목록
     */
    #renameFiles(files: FileList | globalThis.File[]): globalThis.File[] {
        if (this.imageName === null) {
            return Array.from(files);
        }

        return Array.from(files).map((file) => {
            const fileName = this.#buildFileName(file.name, file.type);
            return new globalThis.File([file], fileName, { type: file.type });
        });
    }

    /**
     * editable 모드에서 preview DOM 의 실제 렌더링 크기를 가져온다.
     * imageWidth/imageHeight × previewScale 로 계산한 크기가 CSS max-width 등에 의해
     * 실제로는 더 작게(또는 비율에 맞춰 다르게) 렌더링될 수 있으므로, 가능하면 DOM에서 직접 측정한다.
     *
     * @returns preview 실제 크기
     */
    #getPreviewSize(): { width: number; height: number } {
        const $preview = this.$getPreview();
        const measuredWidth = $preview.getInnerWidth();
        const measuredHeight = $preview.getInnerHeight();

        return {
            width: measuredWidth > 0 ? measuredWidth : this.imageWidth * this.previewScale,
            height: measuredHeight > 0 ? measuredHeight : this.imageHeight * this.previewScale,
        };
    }
}
